/* eslint-disable no-console */
import {Logger} from '@practicegenius/domain/src/common/logger';
import {isClient} from '@practicegenius/pg-ui/src/utils/environment';
import * as R from 'ramda';
import Rollbar, {LogArgument} from 'rollbar';

type Level = keyof Logger;
interface RollbarWindow extends Window {
  Rollbar?: Rollbar;
}
type LoggerConfig = {
  serverAccessToken: string;
  clientAccessToken: string;
  logLevel: keyof Logger;
  isDev: boolean;
  appVersion: string;
};

export const makeNextLogger = (config: LoggerConfig) => {
  const rollbar: Rollbar | null = (() => {
    const {serverAccessToken} = config;
    const {clientAccessToken} = config;

    // Rollbar is always disabled in dev mode
    if (config.isDev) return null;

    if (!R.isEmpty(serverAccessToken) && !isClient) {
      console.log('Setting up Rollbar for Next.js server');
      return new Rollbar({
        accessToken: serverAccessToken,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
          environment: 'production',
          poweredBy: `Next.js server`,
        },
        codeVersion: config.appVersion,
      });
    }

    // On the client, we'll use the global rollbar instance setup in _document.tsx
    if (!R.isEmpty(clientAccessToken) && isClient) {
      const _window = window as RollbarWindow;
      if (!_window.Rollbar) return null;

      return _window.Rollbar;
    }

    return null;
  })();

  const consoleLogLevelMap = {
    debug: 'debug',
    info: 'info',
    warning: 'warn',
    error: 'error',
    critical: 'error',
  } as const;

  const logFn = (level: Level, message: string, data?: LogArgument) => {
    // only send error logs to rollbar
    if (rollbar && (level === 'error' || level === 'critical')) {
      rollbar[level](message, data, err => {
        if (err) console.warn('Error logging to rollbar', message, data, err);
      });
    }

    console[consoleLogLevelMap[level]](message, data ?? '');
  };

  const logger: Logger = {
    debug(message: string, data?: LogArgument) {
      if (config.logLevel !== 'debug') return;
      logFn('debug', message, data);
    },

    info(message: string, data?: LogArgument) {
      logFn('info', message, data);
    },

    warning(message: string, data?: LogArgument) {
      logFn('warning', message, data);
    },

    error(message: string, data?: LogArgument) {
      logFn('error', message, data);
    },

    critical(message: string, data?: LogArgument) {
      logFn('critical', message, data);
    },
  };

  return logger;
};

export const setUserInfoInWindowRollbar = (userId: string) => {
  const _window = window as RollbarWindow;
  if (!_window.Rollbar) return;

  _window.Rollbar.configure({
    payload: {
      person: {
        id: userId,
      },
    },
  });
};
